<template>
  <div class="tf-dash" :data-theme="darkMode ? 'dark' : 'light'" :style="cssVars">

    <!-- ── Topbar ─────────────────────────────────────────── -->
    <header class="topbar">
      <div class="brand">
        <div class="brand-mark">TF</div>
        <div class="brand-text">
          <div class="brand-name">TalentFind</div>
          <div class="brand-sub">{{ isEducationalOrg ? 'Learning Readiness &amp; Risk' : 'Workforce Supply &amp; Risk' }}</div>
        </div>
      </div>
      <div class="title-block">
        <h1>{{ isEducationalOrg ? 'Learner Dashboard' : 'Workforce Dashboard' }}</h1>
        <p class="decision-line">
          Identify risk · find the cause · prioritise action —
          <em>{{ isEducationalOrg ? 'learning readiness tracker' : 'not a recruitment tool' }}</em>
        </p>
      </div>
      <div class="topbar-right">
        <div class="period-wrap" @click.stop>
          <button
            class="selector"
            :class="{ 'selector-open': periodMenuOpen }"
            @click="periodMenuOpen = !periodMenuOpen"
          >
            {{ activePeriodLabel }} <span class="caret">▾</span>
          </button>
          <div v-if="periodMenuOpen" class="period-menu">
            <button
              v-for="opt in PERIOD_OPTIONS"
              :key="opt.key"
              class="period-opt"
              :class="{ active: selectedPeriod === opt.key }"
              @click="selectedPeriod = opt.key; periodMenuOpen = false"
            >{{ opt.label }}</button>
          </div>
        </div>
        <button
          class="theme-toggle"
          :title="darkMode ? 'Switch to light mode' : 'Switch to dark mode'"
          @click="darkMode = !darkMode"
        >
          <!-- sun icon -->
          <svg v-if="darkMode" width="18" height="18" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="5"/>
            <line x1="12" y1="1" x2="12" y2="3"/>
            <line x1="12" y1="21" x2="12" y2="23"/>
            <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
            <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
            <line x1="1" y1="12" x2="3" y2="12"/>
            <line x1="21" y1="12" x2="23" y2="12"/>
            <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
            <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
          </svg>
          <!-- moon icon -->
          <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 12.79A9 9 0 1 1 11.21 3a7 7 0 0 0 9.79 9.79z"/>
          </svg>
        </button>
      </div>
    </header>

    <main class="board">

      <!-- ── KPI Tiles ──────────────────────────────────────── -->
      <div class="kpi-row">
        <button
          v-for="kpi in KPIS"
          :key="kpi.key"
          class="kpi"
          :class="{ 'kpi-on': isKpiActive(kpi), 'kpi-disabled': kpi.disabled }"
          :title="kpi.note"
          :disabled="kpi.disabled"
          @click="!kpi.disabled && handleKpiClick(kpi)"
        >
          <div class="kpi-top">
            <span class="kpi-label">{{ kpi.label }}</span>
            <span class="dot" :class="'dot-' + kpi.rag"></span>
          </div>
          <div class="kpi-value-row">
            <span class="kpi-value">{{ kpi.value }}</span>
            <!-- 4-color readiness bar -->
            <span v-if="kpi.segments" class="readiness-bar" aria-hidden="true">
              <span
                v-for="seg in kpi.segments"
                :key="seg.key"
                class="readiness-seg"
                :style="{ width: seg.pct + '%', background: seg.color }"
              ></span>
            </span>
            <!-- Gauge with needle (other tiles) -->
            <span v-else-if="kpi.gauge != null" class="gauge" aria-hidden="true">
              <span class="gauge-seg gauge-r"></span>
              <span class="gauge-seg gauge-a"></span>
              <span class="gauge-seg gauge-g"></span>
              <span class="gauge-needle" :style="{ left: kpi.gauge + '%' }"></span>
            </span>
          </div>
          <div class="kpi-foot">
            <span v-if="kpi.delta" class="kpi-delta">
              <span class="delta" :class="kpi.deltaDir === 'up' || kpi.deltaDir === 'down-good' ? 'delta-good' : 'delta-bad'">
                <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
                  <path v-if="kpi.deltaDir === 'up' || kpi.deltaDir === 'up-good'" d="M5 1 L9 7 L1 7 Z"/>
                  <path v-else d="M5 9 L1 3 L9 3 Z"/>
                </svg>
              </span>
              <span>{{ kpi.delta }}</span>
              <span v-if="kpi.sub" class="kpi-sub">{{ kpi.sub }}</span>
            </span>
            <span v-else class="kpi-sub">{{ kpi.sub || 'of demand' }}</span>
            <!-- Sparkline -->
            <svg class="spark" width="64" height="22" :viewBox="`0 0 64 22`">
              <path :d="sparkArea(kpi.spark, kpi.rag)" class="spark-fill" :class="'rag-fill-' + kpi.rag"/>
              <path :d="sparkLine(kpi.spark)" class="spark-line" :class="'rag-stroke-' + kpi.rag"/>
            </svg>
          </div>
        </button>
      </div>

      <!-- ── Charts Row ─────────────────────────────────────── -->
      <div class="charts-row">

        <!-- Pipeline Funnel -->
        <div class="card chart-card">
          <div class="card-head">
            <h3>Pipeline — supply stages</h3>
            <span class="card-hint">click a stage to drill in</span>
          </div>
          <div class="funnel-body">
            <!-- Trapezoid shapes -->
            <div class="funnel-shape-col">
              <button
                v-for="(s, i) in mainStages"
                :key="s.key"
                class="funnel-band"
                :class="{
                  on: isStageActive(s.key),
                  dim: filter.type === 'stage' && !isStageActive(s.key) ||
                       (filter.type === 'reason' || filter.type === 'window') && filter.type !== 'stage'
                }"
                :style="{
                  background: s.color,
                  clipPath: funnelClip(i),
                  zIndex: mainStages.length - i
                }"
                @click="onSelect({ type: 'stage', value: s.key })"
              >
                <span class="funnel-count">{{ filteredStageCounts[s.key] }}</span>
              </button>
            </div>
            <!-- Stage labels -->
            <div class="funnel-label-col">
              <button
                v-for="s in mainStages"
                :key="s.key"
                class="funnel-label-btn"
                :class="{
                  on: isStageActive(s.key),
                  dim: filter.type === 'stage' && !isStageActive(s.key)
                }"
                @click="onSelect({ type: 'stage', value: s.key })"
              >
                <span class="exit-dot" :style="{ background: s.color }"></span>
                <span class="funnel-label-name">{{ stageName(s.key) }}</span>
              </button>
            </div>
          </div>
          <!-- Exit rows: Started / Withdrawn -->
          <div class="funnel-exits">
            <button
              v-for="s in exitStages"
              :key="s.key"
              class="funnel-exit"
              :class="{
                on: isStageActive(s.key),
                dim: filter.type === 'stage' && !isStageActive(s.key)
              }"
              @click="onSelect({ type: 'stage', value: s.key })"
            >
              <span class="exit-dot" :style="{ background: s.color }"></span>
              <span class="exit-name">{{ s.name }}</span>
              <span class="exit-count">{{ filteredStageCounts[s.key] }}</span>
            </button>
          </div>
        </div>

        <!-- Risk Reasons -->
        <div class="card chart-card">
          <div class="card-head">
            <h3>Risk Reasons</h3>
            <span class="card-hint">{{ totalRisk }} flagged</span>
          </div>
          <div class="hbars">
            <div
              v-for="r in REASONS"
              :key="r.key"
              class="hbar"
            >
              <span class="hbar-label">{{ r.name }}</span>
              <span class="hbar-track">
                <span
                  class="hbar-fill"
                  :class="'rag-bg-' + r.rag"
                  :style="{ width: Math.max(4, (filteredReasonCounts[r.key] / maxReasonCount) * 100) + '%' }"
                ></span>
              </span>
              <span class="hbar-val">{{ filteredReasonCounts[r.key] }}</span>
            </div>
          </div>
        </div>

        <!-- Predicted Starters -->
        <div class="card chart-card">
          <div class="card-head">
            <h3>Predicted Starters</h3>
            <span class="card-hint">by readiness window</span>
          </div>
          <div class="vbars">
            <button
              v-for="w in WINDOWS"
              :key="w.key"
              class="vbar"
              :class="{
                on: filter.type === 'window' && filter.value === w.key,
                dim: filter.type === 'window' && filter.value !== w.key
              }"
              @click="onSelect({ type: 'window', value: w.key })"
            >
              <span class="vbar-col">
                <span class="vbar-num">{{ filteredWindowCounts[w.key] }}</span>
                <span
                  class="vbar-fill"
                  :class="'rag-bg-' + w.rag"
                  :style="{ height: Math.max(6, (filteredWindowCounts[w.key] / maxWindowCount) * 100) + '%' }"
                ></span>
              </span>
              <span class="vbar-label">{{ w.key }} days</span>
            </button>
          </div>
        </div>

      </div><!-- /charts-row -->

      <!-- ── Candidate List ──────────────────────────────────── -->
      <div class="card list-card">
        <div class="list-head">
          <div class="list-head-left">
            <h2>Candidate List</h2>
            <span v-if="filterLabel" class="chip">
              <span class="chip-k">Filtered:</span> {{ filterLabel }}
              <button class="chip-x" aria-label="Clear filter" @click="clearFilter">×</button>
            </span>
          </div>
          <div class="list-head-right">
            <div class="list-search">
              <svg class="search-icon" width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <circle cx="6.5" cy="6.5" r="4.5" stroke="currentColor" stroke-width="1.6"/>
                <path d="M10.5 10.5 L14 14" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
              </svg>
              <input
                v-model="searchQuery"
                class="search-input"
                type="text"
                placeholder="Search name or program…"
                aria-label="Search candidates"
              />
              <button v-if="searchQuery" class="search-clear" aria-label="Clear search" @click="searchQuery = ''">×</button>
            </div>
            <span class="count">{{ filteredCandidates.length.toLocaleString() }} candidates</span>
            <div class="sort-panel-wrap" @click.stop>
              <button
                class="sort-toggle-btn"
                :class="{ open: sortPanelOpen }"
                @click="sortPanelOpen = !sortPanelOpen"
              >
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <path d="M2 4h12M4 8h8M6 12h4" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/>
                </svg>
                Sort
                <span v-if="activeSortLabel" class="sort-active-hint">{{ activeSortLabel }}</span>
              </button>

              <div v-if="sortPanelOpen" class="sort-panel">
                <div class="sort-panel-section-label">Sort by</div>
                <div class="sort-panel-fields">
                  <button
                    v-for="f in SORT_FIELDS"
                    :key="f.key"
                    class="sort-field-opt"
                    :class="{ active: sort.key === f.key }"
                    @click="setSortField(f.key)"
                  >
                    <span class="sort-field-radio"></span>
                    {{ f.label }}
                  </button>
                </div>
                <div class="sort-panel-divider"></div>
                <div class="sort-panel-section-label">Direction</div>
                <div class="sort-panel-dirs">
                  <button
                    class="sort-dir-opt"
                    :class="{ active: sort.dir === 1 }"
                    @click="setSortDir(1)"
                  >↑ Ascending</button>
                  <button
                    class="sort-dir-opt"
                    :class="{ active: sort.dir === -1 }"
                    @click="setSortDir(-1)"
                  >↓ Descending</button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="table-scroll">
          <table class="ctable">
            <thead>
              <tr>
                <th
                  v-for="col in currentColumns"
                  :key="col.key"
                  :class="{ sorted: sort.key === col.key, center: col.align === 'center' }"
                  @click="applySort(col.key)"
                >
                  <span>{{ col.label }}</span>
                  <span class="th-arrow">{{ sort.key === col.key ? (sort.dir === 1 ? '↑' : '↓') : '' }}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              <template v-for="c in paginatedCandidates">
                <tr
                  :key="'row-' + c.id"
                  class="cand-row"
                  :class="{ expanded: expandedId === c.id && c.hasMultiple, 'cand-row-multi': c.hasMultiple }"
                  @click="c.hasMultiple && toggleExpand(c.id)"
                >
                  <!-- ID -->
                  <td v-if="hasCol('id')" class="mono">
                    <span
                      v-if="c.hasMultiple"
                      class="dot dot-green"
                      :title="'This candidate has ' + c.subRows.length + (isEducationalOrg ? ' course' : ' job') + ' records'"
                      style="vertical-align:middle;margin-right:4px;"
                    ></span>{{ c.id }}</td>
                  <!-- Learner (name + role) -->
                  <td v-if="hasCol('learner')">
                    <div class="cell-learner">
                      <div>
                        <div class="learner-name">{{ c.name }}</div>
                        <div class="learner-role">{{ c.role }}</div>
                      </div>
                    </div>
                  </td>
                  <!-- Program / Job Title -->
                  <td v-if="hasCol('program')" class="program-cell">{{ c.program }}</td>
                  <!-- Current Stage -->
                  <td v-if="hasCol('stage')">
                    <span class="badge">{{ stageName(c.stage) }}</span>
                  </td>
                  <!-- Risk Reason -->
                  <td v-if="hasCol('reason')">
                    <span v-if="c.reason" class="reason-tag" :class="'rag-text-' + c.rag">
                      <span class="dot" :class="'dot-' + c.rag" style="width:7px;height:7px;"></span>
                      {{ reasonName(c.reason) }}
                    </span>
                    <span v-else class="muted">—</span>
                  </td>
                  <!-- Action Needed -->
                  <td v-if="hasCol('action')">
                    <span :class="c.atRisk ? 'action-flag' : 'muted'">{{ c.action }}</span>
                  </td>
                  <!-- Completion Date -->
                  <td v-if="hasCol('completion')" class="mono-d">{{ c.completion }}</td>
                  <!-- Employer Linked (parent users only) -->
                  <td v-if="hasCol('linked')">
                    <span v-if="c.linked" class="partner-org-name">{{ c.linked }}</span>
                    <span v-else class="muted">—</span>
                  </td>
                  <!-- Entered Stage -->
                  <td v-if="hasCol('entered')" class="mono-d">{{ c.enteredStage }}</td>
                  <!-- Predicted Start -->
                  <td v-if="hasCol('predicted')">
                    <span v-if="c.predictedStart" class="mono-d strong">{{ c.predictedStart }}</span>
                    <span v-else class="muted">—</span>
                  </td>
                </tr>

                <!-- Multi-course expanded row (educational orgs) -->
                <tr v-if="isEducationalOrg && expandedId === c.id && c.hasMultiple" :key="'courses-' + c.id" class="jobs-row">
                  <td :colspan="currentColumns.length">
                    <div class="jobs-expand">
                      <div class="jobs-expand-head">
                        <span class="dot dot-green" style="width:8px;height:8px;"></span>
                        <strong>{{ c.name }}</strong>
                        <span class="jobs-expand-sub">{{ c.subRows.length }} course records</span>
                        <button class="profile-close-btn" aria-label="Close" @click.stop="expandedId = null">×</button>
                      </div>
                      <table class="jobs-table">
                        <thead>
                          <tr>
                            <th>Course Ref</th>
                            <th>Course Title</th>
                            <th>Start Date</th>
                            <th>End Date</th>
                            <th>Progress</th>
                            <th>Completed</th>
                            <th>Passed</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="(course, ci) in c.subRows" :key="ci" class="jobs-sub-row">
                            <td class="mono">{{ course.course_ref }}</td>
                            <td>{{ course.course_title }}</td>
                            <td class="mono-d">{{ course.start_date || '—' }}</td>
                            <td class="mono-d">{{ course.end_date || '—' }}</td>
                            <td>{{ course.progress }}</td>
                            <td>{{ course.module_completed }}</td>
                            <td>{{ course.module_passed }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </td>
                </tr>

                <!-- Multi-job expanded row (workforce orgs) -->
                <tr v-if="!isEducationalOrg && expandedId === c.id && c.hasMultiple" :key="'jobs-' + c.id" class="jobs-row">
                  <td :colspan="currentColumns.length">
                    <div class="jobs-expand">
                      <div class="jobs-expand-head">
                        <span class="dot dot-green" style="width:8px;height:8px;"></span>
                        <strong>{{ c.name }}</strong>
                        <span class="jobs-expand-sub">{{ c.subRows.length }} job records</span>
                        <button class="profile-close-btn" aria-label="Close" @click.stop="expandedId = null">×</button>
                      </div>
                      <table class="jobs-table">
                        <thead>
                          <tr>
                            <th>Job No</th>
                            <th>Job Title</th>
                            <th>Job Status</th>
                            <th>Match Status</th>
                            <th>Sub Status</th>
                            <th>Applied</th>
                            <th>Joining Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="(job, ji) in c.subRows" :key="ji" class="jobs-sub-row">
                            <td class="mono">{{ job.job_no }}</td>
                            <td>{{ job.job_title }}</td>
                            <td><span class="badge">{{ job.job_status }}</span></td>
                            <td><span class="badge">{{ job.match_status }}</span></td>
                            <td class="muted">{{ job.match_sub_status }}</td>
                            <td class="mono-d">{{ job.job_applied || '—' }}</td>
                            <td class="mono-d">{{ job.joining_date || '—' }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </td>
                </tr>

              </template>

              <tr v-if="filteredCandidates.length === 0">
                <td :colspan="currentColumns.length" class="empty">
                  No candidates match this filter.
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="table-foot">
          <span class="page-info">
            Showing
            {{ ((safePage - 1) * PAGE_SIZE + 1).toLocaleString() }}–{{ Math.min(safePage * PAGE_SIZE, sortedCandidates.length).toLocaleString() }}
            of {{ sortedCandidates.length.toLocaleString() }} candidates
          </span>
          <div class="pagination">
            <button class="page-btn" :disabled="safePage <= 1" aria-label="First page" @click="currentPage = 1">«</button>
            <button class="page-btn" :disabled="safePage <= 1" aria-label="Previous page" @click="currentPage--">‹</button>
            <button
              v-for="p in pageNumbers"
              :key="p"
              class="page-btn page-num"
              :class="{ active: p === safePage }"
              @click="currentPage = p"
            >{{ p }}</button>
            <button class="page-btn" :disabled="safePage >= totalPages" aria-label="Next page" @click="currentPage++">›</button>
            <button class="page-btn" :disabled="safePage >= totalPages" aria-label="Last page" @click="currentPage = totalPages">»</button>
          </div>
        </div>
      </div>

    </main>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import readinessDashboard from '@/services/readinessDashboard'
import { CONSTANT as CONST } from '@/helpers/helper'

// ─── Seeded PRNG (stable dataset across renders) ────────────
function mulberry32(a) {
  return function () {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const rnd = mulberry32(20260604);
const pick = (arr) => arr[Math.floor(rnd() * arr.length)];

// ─── Taxonomy ───────────────────────────────────────────────
const STAGES = [
  { key: "in_pipeline",  name: "In Pipeline",       count: 596, rag: "green", color: "#2475c2", noFunnel: false },
  { key: "nearly_ready", name: "Nearly Ready",      count: 420, rag: "amber", color: "#e0a012", noFunnel: false },
  { key: "ready_now",    name: "Ready Now",         count: 85,  rag: "green", color: "#1f8a5b", noFunnel: false },
  { key: "interviewed",  name: "Interview",         count: 110, rag: "amber", color: "#b05a2f", noFunnel: false },
  { key: "offered",      name: "Offered",           count: 37,  rag: "red",   color: "#c93a2d", noFunnel: false },
  { key: "started",      name: "Started",           count: 219, rag: "green", color: "#6b46c1", noFunnel: true  },
  { key: "withdrawn",    name: "Withdrawn / Vault", count: 48,  rag: "amber", color: "#8a9aad", noFunnel: true  },
];

const REASONS = [
  { key: "language",   name: "Language / OSCE",      count: null, rag: "red"   },
  { key: "eligibility", name: "Eligibility",          count: null, rag: "amber" },
  { key: "assessment", name: "Assessment Failure",    count: 19,   rag: "red"   },
  { key: "placement",  name: "Placement Unallocated", count: 17,   rag: "amber" },
  { key: "attendance", name: "Attendance",            count: 12,   rag: "amber" },
];

const WINDOWS = [
  { key: "30", name: "Next 30 days", count: 24, rag: "green" },
  { key: "60", name: "31–60 days",   count: 30, rag: "amber" },
  { key: "90", name: "61–90 days",   count: 30, rag: "amber" },
];

const ACTIONS = {
  language:   "Book OSCE resit",
  clinical:   "Assign placement hours",
  assessment: "Schedule re-assessment",
  placement:  "Allocate placement site",
  attendance: "Welfare check-in",
};

const ROLES = [
  "Registered Nurse", "Healthcare Assistant", "Theatre Practitioner",
  "Midwife", "Radiographer", "Physiotherapist", "Pharmacy Technician", "ODP",
  "Mental Health Nurse", "Paramedic",
];

const FIRST_NAMES = [
  "Amara", "Joseph", "Priya", "Daniel", "Grace", "Mohammed", "Chen",
  "Sofia", "Tunde", "Elena", "Rahul", "Maria", "Kwame", "Aisha", "Lukas", "Nia",
  "Omar", "Beatriz", "Sam", "Ling", "Fatima", "Ivan", "Naomi", "Yusuf", "Carla",
  "Diego", "Zara", "Femi", "Hana", "Marco",
];
const LAST_NAMES = [
  "Okafor", "Bennett", "Sharma", "Walsh", "Adeyemi", "Khan", "Wei",
  "Costa", "Mensah", "Nowak", "Patel", "Santos", "Owusu", "Begum", "Novak",
  "Ahmed", "Silva", "Reyes", "Dubois", "Ferreira", "Hassan", "Petrov", "Cole",
  "Lim", "Mwangi", "Brar", "Iqbal", "Osei", "Reed", "Conti",
];

// ─── Date helpers ────────────────────────────────────────────
const TODAY = new Date(2026, 5, 4);
function addDays(base, d) { const n = new Date(base); n.setDate(n.getDate() + d); return n; }
function fmt(date) {
  return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

// ─── Build candidate dataset ─────────────────────────────────
function buildCandidates() {
  const list = [];
  let id = 1240;

  const stagePool = [];
  STAGES.forEach((s) => { for (let i = 0; i < s.count; i++) stagePool.push(s.key); });

  const riskByReason = [];
  REASONS.forEach((r) => { for (let i = 0; i < r.count; i++) riskByReason.push(r.key); });

  const total = stagePool.length;
  const riskIdx = new Set();
  let guard = 0;
  while (riskIdx.size < riskByReason.length && guard < total * 4) {
    guard++;
    const i = Math.floor(rnd() * total);
    if (stagePool[i] === "in_pipeline") continue;
    riskIdx.add(i);
  }
  const riskArr = [...riskIdx];

  const winByKey = [];
  WINDOWS.forEach((w) => { for (let i = 0; i < w.count; i++) winByKey.push(w.key); });
  const winIdx = new Set();
  guard = 0;
  while (winIdx.size < winByKey.length && guard < total * 4) {
    guard++;
    const i = Math.floor(rnd() * total);
    const st = stagePool[i];
    if (st === "interviewed" || st === "offered") winIdx.add(i);
  }
  const winArr = [...winIdx];

  const linkedIdx = new Set();
  guard = 0;
  while (linkedIdx.size < 312 && guard < total * 6) {
    guard++;
    const i = Math.floor(rnd() * total);
    const st = stagePool[i];
    const w = st === "offered" ? 0.9 : st === "interviewed" ? 0.7 : st === "ready_now" ? 0.5 : st === "nearly_ready" ? 0.18 : 0.08;
    if (rnd() < w) linkedIdx.add(i);
  }

  for (let i = 0; i < total; i++) {
    const stage = stagePool[i];
    const ri = riskArr.indexOf(i);
    const reason = ri >= 0 ? riskByReason[ri] : null;
    const wi = winArr.indexOf(i);
    const windowKey = wi >= 0 ? winByKey[wi] : null;
    const linked = linkedIdx.has(i);
    const enteredDaysAgo = Math.floor(rnd() * 90) + 1;
    const compDays = windowKey
      ? Number(windowKey) - Math.floor(rnd() * 12)
      : 30 + Math.floor(rnd() * 160);
    const rag = reason
      ? (REASONS.find((r) => r.key === reason).rag)
      : stage === "offered" ? "red"
      : stage === "interviewed" ? (rnd() < 0.5 ? "amber" : "green")
      : stage === "ready_now" ? "green"
      : stage === "in_pipeline" ? "green"
      : (rnd() < 0.18 ? "amber" : "green");

    list.push({
      id: id++,
      name: pick(FIRST_NAMES) + " " + pick(LAST_NAMES),
      role: pick(ROLES),
      stage,
      reason,
      windowKey,
      linked,
      rag,
      atRisk: !!reason,
      action: reason
        ? ACTIONS[reason]
        : (stage === "offered" ? "Confirm start date"
          : stage === "interviewed" ? "Chase DBS / references"
          : stage === "ready_now" ? "Confirm interview slot"
          : "On track — monitor"),
      enteredStage: fmt(addDays(TODAY, -enteredDaysAgo)),
      enteredSort: enteredDaysAgo,
      completion: fmt(addDays(TODAY, compDays)),
      completionSort: compDays,
      predictedStart: windowKey ? fmt(addDays(TODAY, Number(windowKey) - Math.floor(rnd() * 8))) : null,
    });
  }
  return list;
}

// ─── Precompute data ─────────────────────────────────────────
const ALL_CANDIDATES_MOCK = buildCandidates();
const M_TOTAL     = ALL_CANDIDATES_MOCK.length;
const M_LINKED    = ALL_CANDIDATES_MOCK.filter((c) => c.linked).length;
const M_RISK      = ALL_CANDIDATES_MOCK.filter((c) => c.atRisk).length;
const M_DEMAND    = 694;
const M_READINESS = Math.round((M_LINKED / M_DEMAND) * 100);
const M_PRED60    = ALL_CANDIDATES_MOCK.filter((c) => c.windowKey === "30" || c.windowKey === "60").length;

const ragFromPct = (p) => (p >= 60 ? "green" : p >= 40 ? "amber" : "red");

const KPIS_STATIC = [
  {
    key: "active", label: "Active Candidates",
    delta: "+52", deltaDir: "up", sub: "this week", rag: "green",
    filter: { type: "all" }, gauge: null,
    spark: [1180, 1196, 1205, 1214, 1222, 1235, 1248],
    note: "Total candidates in the supply pipeline.",
  },
  {
    key: "readiness", label: "Readiness",
    delta: null, filter: { type: "readiness" }, gauge: null,
    spark: [38, 40, 41, 43, 44, 44, 45],
    note: "Candidate readiness split: eligible / 6-12 months / 12+ months / incomplete.",
  },
  {
    key: "risk", label: "Roles at Risk",
    delta: "-12", deltaDir: "down-good", sub: "at risk",
    filter: { type: "risk" }, gauge: null,
    spark: [124, 118, 112, 108, 104, 99, 96],
    note: "Candidates not matched to any job or with incomplete readiness status.",
  },
  {
    key: "predicted", label: "Predicted Starters", sub: "next 60 days",
    delta: "+10", deltaDir: "up",
    filter: { type: "predicted" }, gauge: null,
    spark: [40, 42, 45, 48, 50, 52, 54],
    note: "Candidates at interview stage or beyond in the job pipeline.",
  },
  {
    key: "hires", label: "Total Starts / Hires",
    delta: "+18", deltaDir: "up", sub: "placed", rag: "green",
    filter: { type: "hires" }, gauge: null,
    spark: [150, 168, 181, 193, 201, 210, 219],
    note: "Candidates who received an offer (accepted or pending).",
  },
  {
    key: "linked", label: "Linked Candidates", value: M_LINKED,
    delta: "+1", deltaDir: "up", sub: "employer-linked", rag: "green",
    filter: { type: "linked" }, gauge: null,
    spark: [298, 302, 305, 307, 309, 311, 312],
    note: "Candidates matched to a confirmed employer vacancy.",
    disabled: true,
  },
];

const STAGE_NAME_MAP  = Object.fromEntries(STAGES.map((s) => [s.key, s.name]));
const REASON_NAME_MAP = Object.fromEntries(REASONS.map((r) => [r.key, r.name]));
const WINDOW_NAME_MAP = Object.fromEntries(WINDOWS.map((w) => [w.key, w.name]));

const SORT_FIELDS = [
  { key: "learner",    label: "Name" },
  { key: "program",    label: "Program" },
  { key: "completion", label: "Completion Date" },
];

const PERIOD_OPTIONS = [
  { key: '1W', label: '1 Week',   days: 7   },
  { key: '1M', label: '1 Month',  days: 30  },
  { key: '3M', label: '3 Months', days: 90  },
  { key: '6M', label: '6 Months', days: 180 },
];

// ─── Column definitions ──────────────────────────────────────
const ALL_COLS = {
  learner:    { key: "learner",    label: "Candidate",             sortFn: (c) => c.name },
  id:         { key: "id",         label: "Candidate ID",          sortFn: (c) => c.id },
  program:    { key: "program",    label: "Program / Job Title",   sortFn: (c) => c.program || "~" },
  stage:      { key: "stage",      label: "Current Stage",         sortFn: (c) => c.stage },
  reason:     { key: "reason",     label: "Risk Reason",           sortFn: (c) => c.reason || "~" },
  action:     { key: "action",     label: "Action Needed",         sortFn: (c) => c.action },
  completion: { key: "completion", label: "Completion Date",       sortFn: (c) => c.completionSort },
  linked:     { key: "linked",     label: "Employer Linked",       sortFn: (c) => c.linked || "~" },
predicted:  { key: "predicted",  label: "Predicted Start",       sortFn: (c) => c.completionSort },
};

function columnsFor(filter) {
  const t = filter ? filter.type : "all";
  const keys = {
    stage:     ["id", "learner", "program", "stage", "reason", "action", "completion", "linked"],
    reason:    ["id", "learner", "program", "reason", "stage", "action", "completion", "linked"],
    window:    ["id", "learner", "program", "predicted", "stage", "action", "completion", "linked"],
    predicted: ["id", "learner", "program", "predicted", "stage", "action", "completion", "linked"],
    linked:    ["id", "learner", "program", "stage", "reason", "action", "completion", "linked"],
    readiness: ["id", "learner", "program", "stage", "reason", "action", "completion", "linked"],
  };
  return (keys[t] || ["id", "learner", "program", "stage", "reason", "action", "completion", "linked"])
    .map((k) => ALL_COLS[k]);
}

const PAGE_SIZE = 15;

// ─── Sparkline helpers ───────────────────────────────────────
function computeSparkPaths(data) {
  const W = 64, H = 22, pad = 2;
  const min = Math.min(...data), max = Math.max(...data);
  const span = max - min || 1;
  const pts = data.map((v, i) => {
    const x = pad + (i / (data.length - 1)) * (W - pad * 2);
    const y = H - pad - ((v - min) / span) * (H - pad * 2);
    return [x.toFixed(1), y.toFixed(1)];
  });
  const linePath = pts.map((p, i) => (i ? "L" : "M") + p[0] + " " + p[1]).join(" ");
  const areaPath =
    linePath +
    ` L ${pts[pts.length - 1][0]} ${H} L ${pts[0][0]} ${H} Z`;
  return { line: linePath, area: areaPath };
}

export default {
  name: "TalentFindReadinessDashboard",

  data() {
    return {
      // ─── State ──────────────────────────────────────────
      darkMode: false,
      filter: { type: "all" },
      expandedId: null,
      sort: { key: "learner", dir: 1 },
      sortPanelOpen: false,
      currentPage: 1,
      searchQuery: '',
      selectedPeriod: '1W',
      periodMenuOpen: false,
      SORT_FIELDS,
      PERIOD_OPTIONS,
      dashboardData: [],
      dashboardLoading: false,
      dashboardError: null,
      linkedCount: 0,

      // ─── Static config (no mutation needed) ─────────────
      STAGES,
      REASONS,
      WINDOWS,
      PAGE_SIZE,
    };
  },

  computed: {
    ...mapGetters(['getCustomerId', 'getOrgID', 'getOrgTypeIDFromAccessToken', 'getCustomerTypeIDFromAccessToken', 'getIsParentUser']),

    // ─── Period filter ────────────────────────────────────
    periodData() {
      const opt = PERIOD_OPTIONS.find(p => p.key === this.selectedPeriod) || PERIOD_OPTIONS[0]
      const cutoff = new Date()
      cutoff.setDate(cutoff.getDate() - opt.days)
      const dateField = this.isEducationalOrg ? 'enrolled_date' : 'job_applied'
      return this.dashboardData.filter(r => {
        if (!r[dateField]) return true
        const d = new Date(r[dateField])
        return !isNaN(d) && d >= cutoff
      })
    },

    activePeriodLabel() {
      const opt = PERIOD_OPTIONS.find(o => o.key === this.selectedPeriod)
      return opt ? opt.label : 'Period'
    },

    kpiSparklines() {
      const data = this.dashboardData
      if (!data.length) return null

      const opt = PERIOD_OPTIONS.find(p => p.key === this.selectedPeriod) || PERIOD_OPTIONS[0]
      const now = new Date()
      const totalMs = opt.days * 24 * 60 * 60 * 1000
      const cutoffMs = now.getTime() - totalMs
      const N = 7
      const bucketMs = totalMs / N

      const dateField = this.isEducationalOrg ? 'enrolled_date' : 'job_applied'
      const toMs = (r) => {
        if (!r[dateField]) return null
        const d = new Date(r[dateField])
        return isNaN(d) ? null : d.getTime()
      }
      const matchSt = (r, ...vals) => vals.includes((r.match_status     || '').trim().toLowerCase())
      const matchSs = (r, ...vals) => vals.includes((r.match_sub_status || '').trim().toLowerCase())

      const buildSpark = (filterFn) => {
        const qualifying = data.filter(filterFn)
        const buckets = new Array(N).fill(0)
        qualifying.forEach(r => {
          const t = toMs(r)
          // null date or older than period start → candidate was already active at period start → bucket 0
          if (t === null || t <= cutoffMs) {
            buckets[0]++
          } else {
            const idx = Math.min(N - 1, Math.floor((t - cutoffMs) / bucketMs))
            buckets[idx]++
          }
        })
        // cumulative so the curve trends upward
        for (let i = 1; i < N; i++) buckets[i] += buckets[i - 1]
        return buckets
      }

      return {
        active:    buildSpark(() => true),
        readiness: buildSpark(r => r.eligible),
        risk:      buildSpark(r => this.isEducationalOrg ? !!r.eligible_incomplete : !r.job_id),
        predicted: buildSpark(r => this.isEducationalOrg
          ? (r.module_completed || r.module_passed)
          : matchSt(r, 'interview', 'scheduled', 'offered', 'on-boarding')),
        hires:     buildSpark(r => this.isEducationalOrg
          ? !!r.module_passed
          : (matchSt(r, 'offered', 'on-boarding') || matchSs(r, 'accepted'))),
        linked:    buildSpark(() => false),
      }
    },

    liveCandidates() {
      if (!this.dashboardData.length) return []

      const fmtDate = (val) => {
        if (!val) return null
        const d = new Date(val)
        if (isNaN(d)) return null
        return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
      }

      const stageFromStatus = (r) => {
        if (this.isEducationalOrg) {
          if (r.module_passed)                          return 'started'
          if (r.module_completed && r.moodle_course_id) return 'offered'
          if (r.module_completed)                       return 'interviewed'
          if (r.eligible_12_plus)                       return 'ready_now'
          if (r.eligible_6_12)                          return 'nearly_ready'
          return 'in_pipeline'
        }
        const s  = (r.match_status     || '').trim().toLowerCase()
        const ss = (r.match_sub_status || '').trim().toLowerCase()
        if (ss === 'accepted')                        return 'started'
        if (s === 'interview' || s === 'scheduled')   return 'interviewed'
        if (s === 'offered'   || s === 'on-boarding') return 'offered'
        if (s === 'withdrawn')                        return 'withdrawn'
        if (r.eligible_12_plus)                       return 'ready_now'
        if (r.eligible_6_12)                          return 'nearly_ready'
        return 'in_pipeline'
      }

      const actionFromStatus = (matchStatus, matchSubStatus) => {
        const s  = (matchStatus    || '').trim().toLowerCase()
        const ss = (matchSubStatus || '').trim().toLowerCase()
        if (s === 'interview')  return 'Attend interview'
        if (s === 'scheduled')  return 'Attend scheduled interview'
        if (s === 'offered' && ss === 'accepted') return 'Complete onboarding'
        if (s === 'offered')    return 'Confirm start date'
        if (s === 'on-boarding') return 'Complete onboarding'
        if (s === 'submitted')  return 'Application being reviewed'
        if (s === 'withdrawn')  return '—'
        return '—'
      }

      const actionFromLearnerRecord = (r) => {
        if (r.module_passed)              return 'Module passed — archive'
        if (r.module_completed)           return 'Awaiting pass confirmation'
        if ((r.overall_progress || 0) > 0) return 'Complete the course'
        if (r.eligible_incomplete)        return 'Complete outstanding requirements'
        return '—'
      }

      // Group all rows by candidate_uid so each candidate appears once
      const groups = {}
      this.dashboardData.forEach((r, i) => {
        const key = r.candidate_uid != null ? String(r.candidate_uid) : `_null_${i}`
        if (!groups[key]) groups[key] = []
        groups[key].push(r)
      })

      return Object.values(groups).map((records, i) => {
        const r = records[0]
        const hasMultiple = records.length > 1

        // Pick the most meaningful reason across all records
        const reason = records.reduce((found, rec) => {
          if (found) return found
          if (this.isEducationalOrg) {
            return rec.language_status != null ? 'language'
                 : rec.eligible_incomplete      ? 'eligibility'
                 : null
          }
          return rec.language_status != null ? 'language'
               : rec.pqr_status      != null ? 'eligibility'
               : null
        }, null)

        const stage = stageFromStatus(r)
        const windowKey = (() => {
          if (this.isEducationalOrg) {
            if (!r.enrolled_date) return null
            const daysSince = Math.floor((Date.now() - new Date(r.enrolled_date).getTime()) / 86400000)
            if (daysSince <= 30) return '30'
            if (daysSince <= 60) return '60'
            if (daysSince <= 90) return '90'
            return null
          }
          return r.eligible        ? '30'
               : r.eligible_6_12   ? '60'
               : r.eligible_12_plus ? '90'
               : null
        })()
        const ragDef = reason
          ? ((REASONS.find(rr => rr.key === reason) || {}).rag || 'amber')
          : stage === 'offered'     ? 'red'
          : stage === 'interviewed' ? 'amber'
          : 'green'

        const subRows = this.isEducationalOrg
          ? records.map(rec => ({
              course_ref:       rec.course_ref      || '—',
              course_title:     rec.course_title     || '—',
              start_date:       fmtDate(rec.start_date),
              end_date:         fmtDate(rec.end_date),
              progress:         rec.overall_progress != null ? rec.overall_progress + '%' : '—',
              module_completed: rec.module_completed ? 'Yes' : 'No',
              module_passed:    rec.module_passed    ? 'Yes' : 'No',
            }))
          : records.map(rec => ({
              job_id:           rec.job_id,
              job_no:           rec.job_no   || rec.display_uid || '—',
              job_title:        rec.job_title || rec.course_title || '—',
              job_status:       rec.job_status    || '—',
              match_status:     rec.match_status  || '—',
              match_sub_status: rec.match_sub_status || '—',
              job_applied:      fmtDate(rec.job_applied),
              joining_date:     fmtDate(rec.joining_date),
            }))

        return {
          id:             r.candidate_uid != null ? r.candidate_uid : i,
          name:           [r.first_name, r.surname].filter(Boolean).join(' ') || '—',
          role:           this.isEducationalOrg
                            ? (r.candidate_type || r.qualification || '—')
                            : (r.current_job || '—'),
          program:        hasMultiple
                            ? `${records.length} ${this.isEducationalOrg ? 'courses' : 'jobs'} matched`
                            : (this.isEducationalOrg ? (r.course_title || '—') : (r.job_title || r.course_title || '—')),
          stage,
          reason,
          windowKey,
          linked:         r.partner_org_name || null,
          rag:            ragDef,
          dotColor:       hasMultiple ? 'green' : ragDef,
          hasMultiple,
          subRows,
          atRisk:         !!reason,
          action:         this.isEducationalOrg
                            ? actionFromLearnerRecord(r)
                            : actionFromStatus(r.match_status, r.match_sub_status),
          enteredStage:   fmtDate(this.isEducationalOrg ? r.enrolled_date : r.job_applied),
          enteredSort:    this.isEducationalOrg
                            ? (r.enrolled_date ? new Date(r.enrolled_date).getTime() : 0)
                            : (r.job_applied   ? new Date(r.job_applied).getTime()   : 0),
          completion:     this.isEducationalOrg
                            ? fmtDate(r.study_end_date || r.end_date)
                            : fmtDate(r.joining_date || r.job_applied),
          completionSort: this.isEducationalOrg
                            ? (r.study_end_date ? new Date(r.study_end_date).getTime() : r.end_date ? new Date(r.end_date).getTime() : 0)
                            : (r.joining_date   ? new Date(r.joining_date).getTime()   : 0),
          predictedStart: null,
        }
      })
    },

    ALL_CANDIDATES() {
      return this.liveCandidates.length ? this.liveCandidates : (this.isEducationalOrg ? [] : ALL_CANDIDATES_MOCK)
    },

    totalHiresCount() {
      return this.liveCandidates.filter(c => c.stage === 'offered' || c.stage === 'started').length
    },

    predictedStartersCount() {
      return this.liveCandidates.filter(c =>
        c.stage === 'interviewed' || c.stage === 'offered' || c.stage === 'started'
      ).length
    },

    atRiskCount() {
      return this.liveCandidates.filter(c => c.atRisk).length
    },

    readinessStats() {
      const total = this.dashboardData.length
      if (!total) return { pct: 0, rag: 'red', segments: [] }
      const eligible          = this.dashboardData.filter(r => r.eligible).length
      const eligible6_12      = this.dashboardData.filter(r => r.eligible_6_12).length
      const eligible12Plus    = this.dashboardData.filter(r => r.eligible_12_plus).length
      const eligibleIncomplete = this.dashboardData.filter(r => r.eligible_incomplete).length
      const pct = Math.round((eligible / total) * 100)
      return {
        pct,
        rag: ragFromPct(pct),
        segments: [
          { key: 'eligible',    pct: Math.round((eligible          / total) * 100), color: 'var(--rag-green)' },
          { key: 'eligible612', pct: Math.round((eligible6_12      / total) * 100), color: 'var(--rag-amber)' },
          { key: 'eligible12p', pct: Math.round((eligible12Plus    / total) * 100), color: '#1a73e8'          },
          { key: 'incomplete',  pct: Math.round((eligibleIncomplete / total) * 100), color: 'var(--rag-red)'  },
        ],
      }
    },

    kpiDeltas() {
      const data = this.dashboardData
      if (!data.length) return null

      const opt = PERIOD_OPTIONS.find(p => p.key === this.selectedPeriod) || PERIOD_OPTIONS[0]
      const cutoffMs = Date.now() - opt.days * 24 * 60 * 60 * 1000
      const dateField = this.isEducationalOrg ? 'enrolled_date' : 'job_applied'
      const inPeriod = (r) => {
        if (!r[dateField]) return false
        const d = new Date(r[dateField])
        return !isNaN(d) && d.getTime() >= cutoffMs
      }
      const matchSt = (r, ...vals) => vals.includes((r.match_status     || '').trim().toLowerCase())
      const matchSs = (r, ...vals) => vals.includes((r.match_sub_status || '').trim().toLowerCase())

      const sub = { '1W': 'this week', '1M': 'this month', '3M': 'this quarter', '6M': 'last 6 months' }[opt.key] || opt.label.toLowerCase()
      const fmt = (n) => n > 0 ? `+${n}` : `${n}`

      return {
        active:    { delta: fmt(data.filter(inPeriod).length),                                                                                                                                              deltaDir: 'up',   sub },
        readiness: { delta: fmt(data.filter(r => inPeriod(r) && r.eligible).length),                                                                                                                        deltaDir: 'up',   sub },
        risk:      { delta: fmt(data.filter(r => inPeriod(r) && (this.isEducationalOrg ? !!r.eligible_incomplete : !r.job_id)).length),                                                                     deltaDir: 'down', sub: 'at risk' },
        predicted: { delta: fmt(data.filter(r => inPeriod(r) && (this.isEducationalOrg ? (r.module_completed || r.module_passed) : matchSt(r, 'interview', 'scheduled', 'offered', 'on-boarding'))).length), deltaDir: 'up',   sub },
        hires:     { delta: fmt(data.filter(r => inPeriod(r) && (this.isEducationalOrg ? !!r.module_passed : (matchSt(r, 'offered', 'on-boarding') || matchSs(r, 'accepted')))).length),                   deltaDir: 'up',   sub },
      }
    },

    KPIS() {
      const sparks = this.kpiSparklines
      const deltas = this.kpiDeltas
      return KPIS_STATIC.map((kpi) => {
        const liveSpark = (sparks && sparks[kpi.key]) ? sparks[kpi.key] : kpi.spark
        const d = deltas && deltas[kpi.key]
        let base = { ...kpi, spark: liveSpark, ...(d ? { delta: d.delta, deltaDir: d.deltaDir, sub: d.sub } : {}) }
        if (kpi.key === 'predicted' && this.isEducationalOrg) {
          base = { ...base, label: 'Predicted Completions', note: 'Learners who completed or passed a module.' }
        }
        if (kpi.key === 'hires' && this.isEducationalOrg) {
          base = { ...base, label: 'Total Passes', note: 'Learners who passed the module.' }
        }
        if (kpi.key === 'active') {
          return { ...base, value: this.dashboardData.length.toLocaleString() }
        }
        if (kpi.key === 'readiness') {
          const { pct, rag, segments } = this.readinessStats
          return { ...base, value: pct + '%', rag, segments }
        }
        if (kpi.key === 'risk') {
          const count = this.atRiskCount
          return { ...base, value: count, rag: count > 80 ? 'red' : 'amber' }
        }
        if (kpi.key === 'predicted') {
          const count = this.predictedStartersCount
          return { ...base, value: count, rag: count >= 50 ? 'green' : 'amber' }
        }
        if (kpi.key === 'hires') {
          const count = this.totalHiresCount
          return { ...base, value: count, rag: count > 0 ? 'green' : 'amber' }
        }
        if (kpi.key === 'linked') {
          if (!this.getIsParentUser) return null
          const count = this.liveCandidates.filter(c => !!c.linked).length
          const flatSpark = new Array(7).fill(count)
          return { ...base, value: count, spark: flatSpark, disabled: false }
        }
        return base
      }).filter(Boolean)
    },

    isEducationalOrg() {
      return this.getCustomerTypeIDFromAccessToken === CONST.CUSTOMER_TYPES.educational_institute
    },

    // ─── CSS variables ────────────────────────────────────
    cssVars() {
      return {
        "--accent":    "#0b5cab",
        "--rag-green": "#1f8a5b",
        "--rag-amber": "#e0a012",
        "--rag-red":   "#d23f31",
        "--radius":    "12px",
        "--radius-sm": "8px",
      };
    },

    // ─── Filtered candidate list ──────────────────────────
    filteredCandidates() {
      const f = this.filter;
      const q = this.searchQuery.trim().toLowerCase();

      let base;
      switch (f && f.type) {
        case "risk":      base = this.ALL_CANDIDATES.filter((c) => c.atRisk); break;
        case "linked":    base = this.ALL_CANDIDATES.filter((c) => c.linked); break;
        case "predicted": base = this.ALL_CANDIDATES.filter((c) => c.stage === 'interviewed' || c.stage === 'offered' || c.stage === 'started'); break;
        case "readiness": base = this.ALL_CANDIDATES.filter((c) => !c.linked); break;
        case "hires":     base = this.ALL_CANDIDATES.filter((c) => c.stage === "offered" || c.stage === "started"); break;
        case "stage":     base = f.value === 'in_pipeline' ? this.ALL_CANDIDATES : this.ALL_CANDIDATES.filter((c) => c.stage === f.value); break;
        case "reason":    base = this.ALL_CANDIDATES.filter((c) => c.reason === f.value); break;
        case "window":    base = this.isEducationalOrg
                            ? this.ALL_CANDIDATES.filter((c) => c.windowKey === f.value)
                            : this.ALL_CANDIDATES.filter((c) => c.windowKey === f.value && (c.stage === 'interviewed' || c.stage === 'offered' || c.stage === 'started')); break;
        default:          base = this.ALL_CANDIDATES;
      }

      if (!q) return base;
      return base.filter((c) =>
        (c.name    || '').toLowerCase().includes(q) ||
        (c.program || '').toLowerCase().includes(q)
      );
    },

    // ─── Live stage counts — derived from grouped candidates so funnel numbers match list ──
    stageCounts() {
      const candidates = this.liveCandidates
      if (!candidates.length) return null
      const m = {}
      STAGES.forEach(s => { m[s.key] = 0 })
      candidates.forEach(c => { if (m[c.stage] !== undefined) m[c.stage]++ })
      // In Pipeline = all active candidates matched to any job (total pipeline)
      m.in_pipeline = candidates.length
      return m
    },

    // ─── Aggregates over filtered set ─────────────────────
    filteredStageCounts() {
      if (this.stageCounts) return this.stageCounts
      const m = {};
      STAGES.forEach((s) => { m[s.key] = 0; });
      this.filteredCandidates.forEach((c) => { if (m[c.stage] !== undefined) m[c.stage]++; });
      return m;
    },

    filteredReasonCounts() {
      const data = this.dashboardData
      const m = {};
      REASONS.forEach((r) => {
        if (r.key === 'language') {
          m[r.key] = data.filter(d => d.language_status != null).length
        } else if (r.key === 'eligibility') {
          m[r.key] = data.filter(d => d.pqr_status != null).length
        } else {
          m[r.key] = r.count || 0
        }
      });
      return m;
    },

    windowCounts() {
      const candidates = this.liveCandidates
      if (!candidates.length) return null
      if (this.isEducationalOrg) {
        return {
          '30': candidates.filter(c => c.windowKey === '30').length,
          '60': candidates.filter(c => c.windowKey === '60').length,
          '90': candidates.filter(c => c.windowKey === '90').length,
        }
      }
      const inPipeline = c => c.stage === 'interviewed' || c.stage === 'offered' || c.stage === 'started'
      return {
        '30': candidates.filter(c => c.windowKey === '30' && inPipeline(c)).length,
        '60': candidates.filter(c => c.windowKey === '60' && inPipeline(c)).length,
        '90': candidates.filter(c => c.windowKey === '90' && inPipeline(c)).length,
      }
    },

    filteredWindowCounts() {
      if (this.windowCounts) return this.windowCounts
      const m = {};
      WINDOWS.forEach((w) => { m[w.key] = 0; });
      this.filteredCandidates.forEach((c) => { if (c.windowKey && m[c.windowKey] !== undefined) m[c.windowKey]++; });
      return m;
    },

    maxReasonCount() {
      return Math.max(1, ...Object.values(this.filteredReasonCounts));
    },

    maxWindowCount() {
      return Math.max(1, ...Object.values(this.filteredWindowCounts));
    },

    totalRisk() {
      return Object.values(this.filteredReasonCounts).reduce((a, v) => a + v, 0);
    },

    // ─── Funnel ───────────────────────────────────────────
    mainStages() {
      return STAGES.filter((s) => !s.noFunnel);
    },

    exitStages() {
      return STAGES.filter((s) => s.noFunnel);
    },

    // ─── Table columns ─────────────────────────────────────
    currentColumns() {
      const cols = columnsFor(this.filter);
      if (!this.getIsParentUser) return cols.filter(c => c.key !== 'linked');
      return cols;
    },

    // ─── Sorted candidates ─────────────────────────────────
    sortedCandidates() {
      const col = ALL_COLS[this.sort.key];
      if (!col) return [...this.filteredCandidates];
      return [...this.filteredCandidates].sort((a, b) => {
        const av = col.sortFn(a), bv = col.sortFn(b);
        if (av < bv) return -1 * this.sort.dir;
        if (av > bv) return 1 * this.sort.dir;
        return a.id - b.id;
      });
    },

    // ─── Pagination ────────────────────────────────────────
    totalPages() {
      return Math.max(1, Math.ceil(this.sortedCandidates.length / PAGE_SIZE));
    },

    safePage() {
      return Math.min(this.currentPage, this.totalPages);
    },

    paginatedCandidates() {
      const start = (this.safePage - 1) * PAGE_SIZE;
      return this.sortedCandidates.slice(start, start + PAGE_SIZE);
    },

    pageNumbers() {
      const total = this.totalPages;
      const current = this.safePage;
      const start = Math.max(1, Math.min(current - 2, total - 4));
      return Array.from({ length: Math.min(5, total) }, (_, i) => start + i);
    },

    activeSortLabel() {
      const f = SORT_FIELDS.find((f) => f.key === this.sort.key);
      if (!f) return null;
      return f.label + (this.sort.dir === 1 ? ' ↑' : ' ↓');
    },

    // ─── Filter label ──────────────────────────────────────
    filterLabel() {
      const f = this.filter;
      if (!f || f.type === "all") return null;
      const labels = {
        risk:      "Roles at Risk",
        linked:    "Employer-linked",
        predicted: "Predicted starters · next 60 days",
        readiness: "Readiness gap · not yet linked",
        hires:     "Confirmed starts / hires",
      };
      if (labels[f.type]) return labels[f.type];
      if (f.type === "stage")  return "Stage · " + (STAGE_NAME_MAP[f.value] || f.value);
      if (f.type === "reason") return "Risk reason · " + (REASON_NAME_MAP[f.value] || f.value);
      if (f.type === "window") return "Predicted · " + (WINDOW_NAME_MAP[f.value] || f.value);
      return null;
    },
  },

  watch: {
    filteredCandidates() {
      this.currentPage = 1;
      this.expandedId = null;
    },
    searchQuery() {
      this.currentPage = 1;
    },
    sortPanelOpen(val) {
      if (val) {
        const close = () => { this.sortPanelOpen = false; document.removeEventListener('click', close); };
        this.$nextTick(() => document.addEventListener('click', close));
      }
    },
    periodMenuOpen(val) {
      if (val) {
        const close = () => { this.periodMenuOpen = false; document.removeEventListener('click', close); };
        this.$nextTick(() => document.addEventListener('click', close));
      }
    },
    selectedPeriod() {
      this.currentPage = 1;
    },
    currentColumns(newCols) {
      if (!newCols.find((c) => c.key === this.sort.key)) {
        this.sort = { key: newCols[0].key, dir: 1 };
      }
    },
  },

  created() {
    this.fetchDashboardData()
  },

  methods: {
    // ─── API ───────────────────────────────────────────────
    async fetchDashboardData() {
      const custId = this.getCustomerId
      const orgId = this.getIsParentUser ? null : this.getOrgID
      if (!custId) return

      this.dashboardLoading = true
      this.dashboardError = null
      try {
        const calls = [
          this.isEducationalOrg
            ? readinessDashboard.getActiveLearnerCandidates(custId, orgId)
            : readinessDashboard.getActiveWorkforceCandidates(custId, orgId),
        ]
        if (this.getIsParentUser) {
          calls.push(
            this.isEducationalOrg
              ? readinessDashboard.getLearnerLinkedCount(custId)
              : readinessDashboard.getLinkedCount(custId)
          )
        }
        const [candidatesRes, linkedRes] = await Promise.all(calls)
        this.dashboardData = candidatesRes.data
        if (linkedRes) this.linkedCount = linkedRes.data.count || 0
      } catch (err) {
        this.dashboardError = err
      } finally {
        this.dashboardLoading = false
      }
    },


    // ─── Filter ────────────────────────────────────────────
    onSelect(f) {
      const isSame = JSON.stringify(this.filter) === JSON.stringify(f);
      this.filter = isSame ? { type: "all" } : f;
    },

    clearFilter() {
      this.filter = { type: "all" };
    },

    isKpiActive(kpi) {
      return this.filter && this.filter.type === kpi.filter.type;
    },

    handleKpiClick(kpi) {
      this.onSelect(kpi.filter);
    },

    isStageActive(key) {
      return this.filter.type === "stage" && this.filter.value === key;
    },

    // ─── Sort ──────────────────────────────────────────────
    applySort(key) {
      if (this.sort.key === key) {
        this.sort = { key, dir: -this.sort.dir };
      } else {
        this.sort = { key, dir: 1 };
      }
      this.currentPage = 1;
    },

    setSortField(key) {
      this.sort = { key, dir: this.sort.dir };
      this.currentPage = 1;
    },

    setSortDir(dir) {
      this.sort = { key: this.sort.key, dir };
      this.currentPage = 1;
    },

    // ─── Expand ────────────────────────────────────────────
    toggleExpand(id) {
      this.expandedId = this.expandedId === id ? null : id;
    },

    // ─── Funnel clip-path ──────────────────────────────────
    funnelClip(i) {
      const TOP = [0, 10, 20, 30, 40];
      const BOT = [7, 17, 27, 37, 46];
      const l = TOP[i], r = BOT[i];
      return `polygon(${l}% 0%, ${100 - l}% 0%, ${100 - r}% 100%, ${r}% 100%)`;
    },

    // ─── Sparkline paths ───────────────────────────────────
    sparkLine(data) {
      return computeSparkPaths(data).line;
    },

    sparkArea(data) {
      return computeSparkPaths(data).area;
    },

    // ─── Column helpers ────────────────────────────────────
    hasCol(key) {
      return this.currentColumns.some((c) => c.key === key);
    },

    stageName(key) {
      if (this.isEducationalOrg) {
        const eduNames = {
          interviewed: 'Completed',
          offered:     'Moodle Completed',
          started:     'Passed',
          withdrawn:   'Withdrawn',
        }
        return eduNames[key] || STAGE_NAME_MAP[key] || key
      }
      return STAGE_NAME_MAP[key] || key
    },
    reasonName(key) { return REASON_NAME_MAP[key] || key; },
  },
};
</script>

<style scoped lang="scss">
// ─── CSS Variables (applied via :style on root) ──────────────
// Declare fallbacks so they work even without the binding
.tf-dash {
  --accent:    #0b5cab;
  --rag-green: #1f8a5b;
  --rag-amber: #e0a012;
  --rag-red:   #d23f31;
  --radius:    12px;
  --radius-sm: 8px;

  // Light theme tokens
  --ink:   #16202e;
  --ink-2: #4a5a6e;
  --ink-3: #8593a4;
  --line:  #e3e9f0;
  --bg:    #eef2f6;
  --card:  #ffffff;
  --sh:    0 1px 2px rgba(16,32,53,.06), 0 4px 16px rgba(16,32,53,.05);
  --sh-on: 0 2px 6px rgba(11,92,171,.18), 0 8px 24px rgba(11,92,171,.14);

  // Dark theme overrides
  &[data-theme="dark"] {
    --ink:   #dde6f0;
    --ink-2: #7a9ab8;
    --ink-3: #3e556a;
    --line:  #1c2c3c;
    --bg:    #0d1520;
    --card:  #122030;
    --sh:    0 1px 3px rgba(0,0,0,.3), 0 4px 16px rgba(0,0,0,.2);
    --sh-on: 0 2px 8px rgba(11,92,171,.35), 0 8px 28px rgba(11,92,171,.2);
  }

  min-height: 100vh;
  background: var(--bg);
  color: var(--ink);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  font-size: 13.5px;
  -webkit-font-smoothing: antialiased;
  padding: 0 26px 40px;

  *, *::before, *::after { box-sizing: border-box; }
  h1, h2, h3 { margin: 0; font-weight: 700; letter-spacing: -.01em; }
  button { font-family: inherit; cursor: pointer; }
}

// ─── Topbar ──────────────────────────────────────────────────
.topbar {
  display: flex;
  align-items: center;
  gap: 28px;
  padding: 18px 4px 16px;
  flex-wrap: wrap;
}

.brand {
  display: flex;
  align-items: center;
  gap: 11px;
}

.brand-mark {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: var(--accent);
  color: #fff;
  display: grid;
  place-items: center;
  font-weight: 800;
  font-size: 16px;
  letter-spacing: -.02em;
  box-shadow: 0 4px 12px rgba(11, 92, 171, .3);
  flex-shrink: 0;
}

.brand-name {
  font-weight: 800;
  font-size: 16px;
  letter-spacing: -.02em;
  line-height: 1.05;
  color: var(--ink);
}

.brand-sub {
  font-size: 11.5px;
  color: var(--ink-3);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: .06em;
}

.title-block {
  flex: 1;
  border-left: 1px solid var(--line);
  padding-left: 26px;

  h1 {
    font-size: 22px;
    line-height: 1;
    color: var(--ink);
  }
}

.decision-line {
  margin: 5px 0 0;
  font-size: 13px;
  color: var(--ink-2);
  font-weight: 500;

  em {
    color: var(--accent);
    font-style: normal;
    font-weight: 700;
  }
}

.topbar-right {
  display: flex;
  gap: 10px;
  align-items: center;
}

.selector {
  background: var(--card);
  border: 1px solid var(--line);
  border-radius: var(--radius-sm);
  padding: 9px 13px;
  font-size: 13px;
  font-weight: 600;
  color: var(--ink-2);
  display: flex;
  align-items: center;
  gap: 7px;
  box-shadow: var(--sh);
  transition: border-color .15s;

  &:hover { border-color: var(--accent); color: var(--ink); }
}

.caret { color: var(--ink-3); font-size: 10px; }

.selector-open { border-color: var(--accent); color: var(--ink); }

.period-wrap {
  position: relative;
}

.period-menu {
  position: absolute;
  right: 0;
  top: calc(100% + 6px);
  background: var(--card);
  border: 1px solid var(--line);
  border-radius: var(--radius-sm);
  box-shadow: 0 8px 24px rgba(16,32,53,.14);
  padding: 5px;
  z-index: 40;
  min-width: 130px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.period-opt {
  background: none;
  border: none;
  text-align: left;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  color: var(--ink-2);
  cursor: pointer;
  transition: background .11s, color .11s;

  &:hover  { background: var(--bg); color: var(--ink); }
  &.active { background: rgba(11,92,171,.09); color: var(--accent); }
}

.theme-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--card);
  border: 1px solid var(--line);
  border-radius: var(--radius-sm);
  width: 38px;
  height: 38px;
  color: var(--ink-2);
  box-shadow: var(--sh);
  transition: background .15s, border-color .15s, color .15s;
  flex-shrink: 0;

  &:hover { border-color: var(--accent); color: var(--ink); }
  svg { display: block; }
}

// ─── Board ────────────────────────────────────────────────────
.board {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

// ─── KPI Tiles ────────────────────────────────────────────────
.kpi-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 14px;

  @media (max-width: 640px) { grid-template-columns: repeat(2, 1fr); }
}

.kpi {
  text-align: left;
  background: var(--card);
  border: 1px solid var(--line);
  border-radius: var(--radius);
  padding: 14px 15px 13px;
  box-shadow: var(--sh);
  transition: transform .14s, box-shadow .14s, border-color .14s;
  display: flex;
  flex-direction: column;
  gap: 9px;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    left: 0; top: 0; bottom: 0;
    width: 3px;
    background: transparent;
    transition: background .15s;
  }

  &:hover { transform: translateY(-2px); box-shadow: var(--sh-on); }

  &.kpi-on {
    border-color: var(--accent);
    box-shadow: var(--sh-on);
    background: linear-gradient(180deg, rgba(11,92,171,.06) 0%, var(--card) 100%);
    &::before { background: var(--accent); }
  }

  &.kpi-disabled {
    opacity: 0.45;
    filter: grayscale(100%);
    cursor: not-allowed;
    pointer-events: none;
    &:hover { transform: none; box-shadow: var(--sh); }
  }
}

.kpi-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.kpi-label {
  font-size: 12px;
  font-weight: 700;
  color: var(--ink-2);
  line-height: 1.15;
  letter-spacing: -.005em;
}

.kpi-value-row {
  display: flex;
  align-items: flex-end;
  gap: 10px;
}

.kpi-value {
  font-size: 30px;
  font-weight: 800;
  letter-spacing: -.025em;
  line-height: .9;
  font-variant-numeric: tabular-nums;
  color: var(--ink);
}

.kpi-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  margin-top: 1px;
}

.kpi-delta {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.kpi-sub {
  color: var(--ink-3);
  font-weight: 600;
  font-size: 11.5px;
}

.delta {
  svg { display: block; }
  &.delta-good svg { fill: var(--rag-green); }
  &.delta-bad  svg { fill: var(--rag-red);   }
}
.delta-good ~ span { color: var(--rag-green); }
.delta-bad  ~ span { color: var(--rag-red);   }

// RAG dots
.dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
  display: inline-block;

  &.dot-green { background: var(--rag-green); box-shadow: 0 0 0 3px rgba(31, 138, 91, .18); }
  &.dot-amber { background: var(--rag-amber); box-shadow: 0 0 0 3px rgba(224, 160, 18, .18); }
  &.dot-red   { background: var(--rag-red);   box-shadow: 0 0 0 3px rgba(210, 63, 49, .18);  }
}

// Gauge
.gauge {
  position: relative;
  flex: 1;
  height: 7px;
  border-radius: 4px;
  overflow: hidden;
  display: flex;
  max-width: 120px;
  margin-bottom: 4px;
}
.gauge-seg { height: 100%; }
.gauge-r   { flex: 0 0 40%; background: var(--rag-red);   }
.gauge-a   { flex: 0 0 20%; background: var(--rag-amber); }
.gauge-g   { flex: 0 0 40%; background: var(--rag-green); }
.gauge-needle {
  position: absolute;
  top: -3px;
  width: 3px;
  height: 13px;
  background: var(--ink);
  border-radius: 2px;
  transform: translateX(-50%);
  box-shadow: 0 0 0 2px var(--card);
}

.readiness-bar {
  flex: 1;
  display: flex;
  height: 7px;
  border-radius: 4px;
  overflow: hidden;
  max-width: 120px;
  margin-bottom: 4px;
  gap: 2px;
}
.readiness-seg {
  height: 100%;
  border-radius: 2px;
  min-width: 2px;
  transition: width 0.3s ease;
}

// Sparklines
.spark { display: block; opacity: .9; }
.spark-line { fill: none; stroke-width: 1.6; }
.spark-fill { opacity: .14; stroke: none; }
.rag-stroke-green { stroke: var(--rag-green); }
.rag-stroke-amber { stroke: var(--rag-amber); }
.rag-stroke-red   { stroke: var(--rag-red);   }
.rag-fill-green   { fill: var(--rag-green); }
.rag-fill-amber   { fill: var(--rag-amber); }
.rag-fill-red     { fill: var(--rag-red);   }

// ─── Charts Row ───────────────────────────────────────────────
.charts-row {
  display: grid;
  grid-template-columns: 1.18fr 1fr 1fr;
  gap: 18px;
  align-items: stretch;

  @media (max-width: 1180px) { grid-template-columns: 1fr; }
}

.card {
  background: var(--card);
  border: 1px solid var(--line);
  border-radius: var(--radius);
  box-shadow: var(--sh);
}

.chart-card {
  padding: 16px 18px 18px;
  display: flex;
  flex-direction: column;
}

.card-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 14px;
  flex-shrink: 0;

  h3 { font-size: 15px; color: var(--ink); }
}

.card-hint {
  font-size: 11.5px;
  color: var(--ink-3);
  font-weight: 600;
}

// ─── Funnel ───────────────────────────────────────────────────
.funnel-body {
  display: flex;
  gap: 14px;
  flex: 1;
  align-items: stretch;
}

.funnel-shape-col {
  display: flex;
  flex-direction: column;
  flex: 0 0 62%;
  gap: 0;
}

.funnel-label-col {
  display: flex;
  flex-direction: column;
  flex: 1;
  justify-content: space-around;
}

.funnel-band {
  width: 100%;
  height: 52px;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: filter .14s;
  margin-bottom: -1px;
  position: relative;

  &:hover { filter: brightness(1.1); }
  &.on { outline: 2px solid rgba(255,255,255,.7); outline-offset: -2px; }
  &.dim { opacity: .35; }
}

.funnel-count {
  color: #fff;
  font-weight: 800;
  font-size: 20px;
  font-variant-numeric: tabular-nums;
  text-shadow: 0 1px 3px rgba(0,0,0,.22);
  pointer-events: none;
}

.funnel-label-btn {
  display: flex;
  align-items: center;
  gap: 9px;
  background: none;
  border: none;
  padding: 6px 8px;
  border-radius: 7px;
  font-size: 12.5px;
  font-weight: 700;
  color: var(--ink-2);
  cursor: pointer;
  text-align: left;
  flex: 1;
  transition: background .12s, color .12s;

  &:hover { background: var(--bg); color: var(--ink); }
  &.on    { color: var(--accent); background: rgba(11, 92, 171, .09); }
  &.dim   { opacity: .35; }
}

.funnel-exits {
  display: flex;
  gap: 10px;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid var(--line);
}

.funnel-exit {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: 1px solid var(--line);
  border-radius: var(--radius-sm);
  padding: 8px 11px;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--ink-2);
  transition: background .12s, border-color .12s;

  &:hover { background: var(--bg); border-color: var(--accent); }
  &.on    { background: rgba(11, 92, 171, .08); border-color: var(--accent); color: var(--ink); }
  &.dim   { opacity: .4; }
}

.exit-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  flex-shrink: 0;
}

.exit-name { flex: 1; text-align: left; }
.exit-count {
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  color: var(--ink);
}

// ─── Risk Reasons (horizontal bars) ──────────────────────────
.hbars {
  display: flex;
  flex-direction: column;
  flex: 1;
  justify-content: space-evenly;
}

.hbar {
  background: none;
  border: none;
  padding: 8px 6px;
  display: grid;
  grid-template-columns: 130px 1fr 30px;
  align-items: center;
  gap: 11px;
  transition: opacity .15s, background .12s;
  border-radius: 6px;

  &:hover { background: var(--bg); }
  &.on    { background: rgba(11, 92, 171, .07); }
  &.dim   { opacity: .4; }
}

.hbar-label {
  font-size: 12.5px;
  font-weight: 600;
  color: var(--ink-2);
  text-align: left;
  white-space: nowrap;
}

.hbar-track {
  display: block;
  height: 14px;
  background: #eef2f7;
  border-radius: 7px;
  overflow: hidden;

  .tf-dash[data-theme="dark"] & { background: #182638; }
}

.hbar-fill {
  display: block;
  height: 100%;
  border-radius: 7px;
  transition: width .4s cubic-bezier(.4, 0, .2, 1);
}

.hbar-val {
  font-size: 13px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  text-align: right;
  color: var(--ink);
}

// RAG background colours
.rag-bg-green { background: var(--rag-green); }
.rag-bg-amber { background: var(--rag-amber); }
.rag-bg-red   { background: var(--rag-red);   }
.rag-text-green { color: var(--rag-green); }
.rag-text-amber { color: color-mix(in srgb, var(--rag-amber) 78%, #7a5500); }
.rag-text-red   { color: var(--rag-red);   }

// ─── Predicted Starters (vertical bars) ──────────────────────
.vbars {
  display: flex;
  gap: 10px;
  align-items: stretch;
  flex: 1;
  padding-top: 4px;
}

.vbar {
  flex: 1;
  background: none;
  border: none;
  padding: 8px 6px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  transition: opacity .15s;
  border-radius: 8px;

  &:hover { background: var(--bg); }
  &.on    { background: rgba(11, 92, 171, .07); }
  &.dim   { opacity: .4; }
}

.vbar-col {
  flex: 1;
  min-height: 100px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  width: 100%;
  gap: 7px;
}

.vbar-num {
  font-size: 18px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  color: var(--ink);
}

.vbar-fill {
  display: block;
  width: 100%;
  max-width: 80px;
  border-radius: 8px 8px 0 0;
  transition: height .4s cubic-bezier(.4, 0, .2, 1);
  min-height: 12px;
}

.vbar-label {
  font-size: 12.5px;
  font-weight: 700;
  color: var(--ink-2);
  white-space: nowrap;
}

// ─── Candidate List ───────────────────────────────────────────
.list-card { padding: 0; overflow: hidden; }

.list-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 18px;
  border-bottom: 1px solid var(--line);
  flex-wrap: wrap;
}

.list-head-left {
  display: flex;
  align-items: center;
  gap: 13px;
  flex-wrap: wrap;

  h2 { font-size: 17px; color: var(--ink); }
}

.list-sub {
  font-size: 12.5px;
  color: var(--ink-3);
  font-weight: 500;
}

.chip {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: rgba(11, 92, 171, .1);
  border: 1px solid rgba(11, 92, 171, .3);
  color: var(--accent);
  font-size: 12.5px;
  font-weight: 700;
  padding: 5px 6px 5px 11px;
  border-radius: 999px;
}

.chip-k { color: rgba(11, 92, 171, .6); font-weight: 600; }

.chip-x {
  background: var(--accent);
  color: #fff;
  border: none;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  font-size: 13px;
  line-height: 1;
  display: grid;
  place-items: center;
}

.list-head-right {
  display: flex;
  align-items: center;
  gap: 14px;
}

.count {
  font-size: 12.5px;
  color: var(--ink-2);
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

// ─── Search bar ───────────────────────────────────────────────
.list-search {
  position: relative;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  left: 10px;
  color: var(--ink-3);
  pointer-events: none;
}

.search-input {
  height: 36px;
  padding: 0 32px 0 32px;
  border: 1px solid var(--line);
  border-radius: var(--radius-sm);
  background: var(--bg);
  color: var(--ink);
  font-size: 13px;
  font-family: inherit;
  width: 220px;
  transition: border-color .14s, box-shadow .14s;
  outline: none;

  &::placeholder { color: var(--ink-3); }
  &:focus { border-color: var(--accent); box-shadow: 0 0 0 3px rgba(11,92,171,.12); background: var(--card); }
}

.search-clear {
  position: absolute;
  right: 8px;
  background: none;
  border: none;
  color: var(--ink-3);
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  padding: 0 2px;

  &:hover { color: var(--ink); }
}

// ─── Sort panel ───────────────────────────────────────────────
.sort-panel-wrap {
  position: relative;
}

.sort-toggle-btn {
  display: flex;
  align-items: center;
  gap: 7px;
  background: var(--accent);
  color: #fff;
  border: none;
  border-radius: var(--radius-sm);
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(11,92,171,.22);
  transition: filter .13s;

  &:hover  { filter: brightness(1.07); }
  &.open   { filter: brightness(.92); }
}

.sort-active-hint {
  background: rgba(255,255,255,.22);
  border-radius: 4px;
  padding: 1px 6px;
  font-size: 11.5px;
  font-weight: 600;
  white-space: nowrap;
}

.sort-panel {
  position: absolute;
  right: 0;
  top: calc(100% + 8px);
  background: var(--card);
  border: 1px solid var(--line);
  border-radius: var(--radius-sm);
  box-shadow: 0 12px 32px rgba(16,32,53,.16);
  padding: 14px;
  z-index: 30;
  min-width: 190px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.sort-panel-section-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .06em;
  color: var(--ink-3);
  margin-bottom: 2px;
}

.sort-panel-fields {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.sort-field-opt {
  display: flex;
  align-items: center;
  gap: 9px;
  background: none;
  border: none;
  text-align: left;
  padding: 8px 10px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  color: var(--ink-2);
  cursor: pointer;
  transition: background .11s, color .11s;

  &:hover  { background: var(--bg); color: var(--ink); }
  &.active { background: rgba(11,92,171,.09); color: var(--accent);
    .sort-field-radio { border-color: var(--accent); background: var(--accent); box-shadow: inset 0 0 0 3px var(--card); }
  }
}

.sort-field-radio {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid var(--line);
  flex-shrink: 0;
  transition: border-color .12s, background .12s;
}

.sort-panel-divider {
  height: 1px;
  background: var(--line);
  margin: 4px 0;
}

.sort-panel-dirs {
  display: flex;
  gap: 6px;
}

.sort-dir-opt {
  flex: 1;
  background: var(--bg);
  border: 1px solid var(--line);
  border-radius: 6px;
  padding: 7px 10px;
  font-size: 12.5px;
  font-weight: 700;
  color: var(--ink-2);
  cursor: pointer;
  transition: border-color .12s, color .12s, background .12s;

  &:hover  { border-color: var(--accent); color: var(--ink); }
  &.active { border-color: var(--accent); background: rgba(11,92,171,.09); color: var(--accent); }
}

// ─── Table ────────────────────────────────────────────────────
.table-scroll { overflow-x: auto; }

.ctable {
  width: 100%;
  border-collapse: collapse;
  font-size: 12.5px;

  th {
    text-align: left;
    font-size: 10.5px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: .04em;
    color: var(--ink-3);
    padding: 8px 10px;
    border-bottom: 1px solid var(--line);
    background: rgba(250, 251, 253, 1);
    white-space: nowrap;
    cursor: pointer;
    user-select: none;
    position: relative;

    &:hover { color: var(--ink-2); }
    &.sorted { color: var(--accent); }
    &.center { text-align: center; }

    .tf-dash[data-theme="dark"] & { background: #0e1c2a; }
  }

  td {
    padding: 7px 10px;
    border-bottom: 1px solid #f0f3f7;
    vertical-align: middle;
    color: var(--ink);

    &.center { text-align: center; }
    &.mono   { font-variant-numeric: tabular-nums; color: var(--ink-2); font-size: 12px; }
    &.mono-d { font-variant-numeric: tabular-nums; color: var(--ink-2); font-size: 12px; }

    .tf-dash[data-theme="dark"] & { border-bottom-color: #172536; }
  }

  tbody tr { transition: background .12s; }
}

.th-arrow { margin-left: 5px; font-weight: 800; }

.cand-row {
  cursor: default;

  &:hover { background: #f7fafd; }
  &.expanded { background: rgba(31, 138, 91, .06); }
  &.expanded:hover { background: rgba(31, 138, 91, .09); }

  .tf-dash[data-theme="dark"] &:hover { background: #162534; }
}

.cand-row-multi {
  cursor: pointer;
}

// ─── Multi-job expanded row ───────────────────────────────────
.jobs-row td {
  padding: 0;
  background: #f4f9f4;
  border-bottom: 2px solid #b8ddb8;
  .tf-dash[data-theme="dark"] & { background: #0e1f0e; border-bottom-color: #2a4a2a; }
}

.jobs-expand {
  padding: 14px 18px;
}

.jobs-expand-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  font-size: 13px;
  color: var(--ink);
}

.jobs-expand-sub {
  font-size: 12px;
  color: var(--ink-3);
  font-weight: 500;
  flex: 1;
}

.profile-close-btn {
  background: none;
  border: none;
  font-size: 18px;
  color: var(--ink-3);
  cursor: pointer;
  line-height: 1;
  padding: 0 4px;
  &:hover { color: var(--ink); }
}

.jobs-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;

  th {
    text-align: left;
    font-size: 10.5px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: .04em;
    color: var(--ink-3);
    padding: 6px 10px;
    border-bottom: 1px solid var(--line);
    white-space: nowrap;
  }
}

.jobs-sub-row {
  td { padding: 6px 10px; border-bottom: 1px solid #e8f2e8; color: var(--ink); }
  &:last-child td { border-bottom: none; }
  &:hover td { background: rgba(31,138,91,.06); }
}

// ─── Inline profile row ───────────────────────────────────────
.profile-row td {
  padding: 0;
  background: rgba(11, 92, 171, .04);
  border-bottom: 2px solid rgba(11, 92, 171, .2);
}

.profile-card {
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  position: relative;
}

.profile-close {
  position: absolute;
  top: 12px;
  right: 14px;

  button {
    background: none;
    border: 1px solid var(--line);
    border-radius: 50%;
    width: 26px;
    height: 26px;
    font-size: 16px;
    color: var(--ink-3);
    display: grid;
    place-items: center;

    &:hover { background: var(--card); color: var(--ink); }
  }
}

.profile-header {
  display: flex;
  align-items: center;
  gap: 11px;
}

.profile-name { font-size: 16px; font-weight: 800; letter-spacing: -.01em; color: var(--ink); }
.profile-role { font-size: 12.5px; color: var(--ink-3); font-weight: 500; margin-top: 2px; }

.profile-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 28px;
}

.pf {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 130px;
}
.pf-wide { flex: 1 0 100%; }
.pf-l {
  font-size: 10.5px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: .06em;
  color: var(--ink-3);
}
.pf-v { font-size: 13.5px; font-weight: 600; color: var(--ink); }

// ─── Table cell elements ──────────────────────────────────────
.cell-learner { display: flex; align-items: center; gap: 7px; }
.learner-name { font-size: 12.5px; font-weight: 700; color: var(--ink); }
.learner-role { font-size: 11px; color: var(--ink-3); font-weight: 500; margin-top: 1px; }
.program-cell { font-size: 12px; color: var(--ink-2); max-width: 160px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.badge {
  display: inline-block;
  background: #eef3f8;
  color: var(--ink-2);
  font-size: 12px;
  font-weight: 700;
  padding: 3px 9px;
  border-radius: 6px;
  white-space: nowrap;

  .tf-dash[data-theme="dark"] & { background: #1a2e42; color: var(--ink-2); }
}

.reason-tag {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-weight: 700;
  font-size: 13px;
}

.action-flag { font-weight: 700; color: var(--ink); }
.muted       { color: var(--ink-3); }
.partner-org-name { font-size: 12px; color: var(--ink-1); font-weight: 500; }
.strong      { color: var(--ink); font-weight: 600; }

.tick {
  fill: none;
  stroke: var(--accent);
  stroke-width: 2.2;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.cross {
  fill: none;
  stroke: var(--ink);
  stroke-width: 2.2;
  stroke-linecap: round;

  .tf-dash[data-theme="dark"] & { stroke: var(--ink-2); }
}

.empty {
  text-align: center;
  color: var(--ink-3);
  padding: 30px;
  font-weight: 600;
}

// ─── Pagination ───────────────────────────────────────────────
.table-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 18px;
  font-size: 12px;
  color: var(--ink-3);
  font-weight: 600;
  background: #fafbfd;
  flex-wrap: wrap;

  .tf-dash[data-theme="dark"] & { background: #0e1c2a; }
}

.page-info { font-variant-numeric: tabular-nums; }

.pagination {
  display: flex;
  align-items: center;
  gap: 4px;
}

.page-btn {
  min-width: 32px;
  height: 32px;
  padding: 0 6px;
  background: var(--card);
  border: 1px solid var(--line);
  border-radius: var(--radius-sm);
  font-size: 13px;
  font-weight: 700;
  color: var(--ink-2);
  display: grid;
  place-items: center;
  transition: background .12s, border-color .12s, color .12s;

  &:hover:not(:disabled) {
    background: var(--bg);
    border-color: var(--accent);
    color: var(--accent);
  }

  &:disabled { opacity: .35; cursor: default; }
  &.active { background: var(--accent); border-color: var(--accent); color: #fff; }

  .tf-dash[data-theme="dark"] & { background: #122030; border-color: #1c2c3c; }
  .tf-dash[data-theme="dark"] &:hover:not(:disabled) { background: #1b2f42; border-color: var(--accent); }
}
</style>
